/**
 * Unit rendering. Soldiers are drawn as a single InstancedMesh per formation —
 * hundreds of men in one draw call, which is the correct answer to
 * formation-scale battles on the web.
 *
 * PLACEHOLDER GEOMETRY: capsule stand-ins. Phase 0 wires real GLBs here
 * (see tools/asset_audit.py + ASSET_PIPELINE below); the instancing approach
 * does not change, only the geometry/material source.
 */
import * as THREE from "three";
import {
  computeFormationSlots,
  computeRoadColumnSlots,
  slotToWorld,
  type Slot,
} from "../sim/formation";
import type { FormationDef, UnitDef } from "../data/types";
import { artKeyFor, type ResolvedModel } from "../data/artmap";
import { extractStaticGeometry, type ModelStore } from "./models";
import { NavSystem, type FlowField } from "../sim/nav";
import type { RoadNetwork } from "../sim/roads";
import {
  AnimController,
  AnimSystem,
  FRAMES_PER_CLIP,
  BONE_COUNT,
  beginMeleeEngagement,
  formationActivity,
  makeMeleeAnimMemory,
  rearmPilum,
  resolveMeleeActivity,
  type MarchPace,
  type MeleeAnimMemory,
} from "./anim";
import {
  attachAnimAttributes,
  buildHumanoidGeometry,
  flagAnimAttributes,
  makeSkinnedMaterial,
  writeAnimPose,
  type AnimAttributes,
} from "./skinned";

/**
 * LOD policy (20k-unit scale): formations nearer than this to the camera
 * render the skinned humanoid; beyond it they render the static capsule
 * (one draw call either way — the swap only changes vertex cost).
 * Hysteresis (±8%) avoids flicker at the boundary.
 */
export const ANIM_LOD_DISTANCE = 130;

/**
 * Flow-field march state: the formation anchor is the pathing agent.
 * The anchor advances along a shared NavSystem flow field; soldiers keep
 * their slot offsets around the moving anchor (local steering only).
 * The anchor is NEVER teleported — see orderFormationMove.
 */
export interface MarchState {
  /** Requested destination (pre-snap; the field holds the snapped goal). */
  destX: number;
  destZ: number;
  /** Facing to assume on arrival. */
  faceHeading: number;
  /** Team key for the shared field cache. */
  team: number;
  /** Shared flow field toward the destination (cached per team+goal). */
  field: FlowField;
  /** The moving anchor soldiers track. Starts at the live anchor. */
  ax: number;
  az: number;
}

/** Context needed to start/advance a flow-field march. */
export interface MarchContext {
  nav: NavSystem;
  team: number;
  simNow: number;
  /** Road network for the road-column layout + speed bonus (optional). */
  roads?: RoadNetwork | null;
}

/**
 * Formation ids that hold shields-up (data-driven from the canonical
 * formations.json ids: testudo / close_ranks / defensive_square /
 * samnite_layered_defence). Their soldiers play defensive_stance when
 * stationary and shield_block when fighting.
 */
export const DEFENSIVE_FORMATION_IDS = new Set([
  "testudo",
  "close_ranks",
  "defensive_square",
  "samnite_layered_defence",
]);

export interface FormationView {
  group: THREE.Group;
  mesh: THREE.InstancedMesh;
  slots: Slot[];
  /** World-space target positions for each soldier (movement goal). */
  goals: { x: number; z: number }[];
  /** Current world-space positions. */
  positions: { x: number; z: number }[];
  heading: number;
  anchor: { x: number; z: number };
  label: string;
  def: FormationDef;
  /** Unit data for combat (null for non-combat formations). */
  unit: UnitDef | null;
  /** 0 = player, 1 = enemy. */
  team: number;
  /** Bounding radius of the formation footprint, for range checks. */
  radius: number;
  /** Art-map key for the unit model (`<canonical_id>@band<band>` or `placeholder:<id>`).
   *  Feeds the future GLB-instancing swap; the instancing approach is unchanged. */
  artKey: string;
  /** True once a vendored GLB's static geometry replaced the placeholder in
   *  the InstancedMesh. Test/smoke probe (Chromium proof). */
  realGeometry: boolean;
  /**
   * Far-LOD static geometry from the highest vendored art-pipeline LOD tier
   * (ResolvedModel.lodPaths). Null when no LOD tier is vendored — the far
   * tier then stays the capsule. One InstancedMesh either way.
   */
  unitLodGeo: THREE.BufferGeometry | null;
  /**
   * Active flow-field march, or null for direct steering. Set by
   * orderFormationMove when a MarchContext is supplied; advanced by
   * updateFormationMovement. Transient — not serialized by save/load.
   */
  march: MarchState | null;
  /**
   * Formation-level animation state (one controller per formation — the
   * 20k-unit scaling rule). Null when no AnimSystem was supplied (tests);
   * the formation then renders capsules only.
   */
  anim: AnimController | null;
  animSys: AnimSystem | null;
  /** Per-instance animation attributes on the skinned geometry. */
  animAttrs: AnimAttributes | null;
  /** Skinned humanoid geometry (near LOD). Null without an AnimSystem. */
  skinGeo: THREE.BufferGeometry | null;
  /** Per-formation skinned material (tinted). */
  skinMat: THREE.MeshStandardMaterial | null;
  /** Far-LOD capsule material (tinted, pre-existing look). */
  capsuleMat: THREE.Material;
  /** Per-instance phase offsets so soldiers don't move in lockstep. */
  animPhase: Float32Array;
  /** Sim-driven animation inputs, set each tick by movement/combat. */
  animMoving: boolean;
  animMelee: boolean;
  /** March pace -> gait clip (march / double_march / run). */
  animPace: MarchPace;
  /** Defensive formation (testudo/close_ranks/...) — shields-up behavior. */
  animDefensive: boolean;
  /** Per-formation melee animation memory (pilum throw / draw transition). */
  meleeMem: MeleeAnimMemory;
  /** True while the anchor is on a road: 4-abreast column + road bonus. */
  roadColumn: boolean;
  /** Current road move-speed bonus at the anchor (1 when off-road). */
  roadBonus: number;
  /**
   * Fired when a soldier's throw clip crosses its release point
   * (pilum volley spawn). Args: anchor x/z + formation facing.
   */
  onRelease: ((x: number, z: number, heading: number) => void) | null;
  /** Current LOD: true = skinned humanoid, false = static capsule. */
  lodNear: boolean;
  /**
   * Real-model geometry path: when the unit's GLB is vendored, its merged
   * static geometry replaces BOTH LOD geometries (one InstancedMesh either
   * way — geometry source only, per render/models.ts). Null until the async
   * load completes; the capsule/skinned fallbacks render meanwhile.
   */
  unitGeo: THREE.BufferGeometry | null;
  unitMat: THREE.Material | null;
  /** The resolved art for this formation's unit (null when unmapped). */
  model: ResolvedModel | null;
}

const dummy = new THREE.Object3D();

/** Shared far-LOD capsule geometry (was allocated per formation). */
const sharedCapsuleGeo = new THREE.CapsuleGeometry(0.32, 1.1, 3, 8);
sharedCapsuleGeo.translate(0, 0.9, 0);

export function createFormationView(
  def: FormationDef,
  label: string,
  count: number,
  anchor: { x: number; z: number },
  heading: number,
  tint: number,
  groundY: (x: number, z: number) => number,
  opts: {
    unit?: UnitDef | null;
    team?: number;
    art?: ResolvedModel;
    anim?: AnimSystem;
    /** Vendored-GLB loader; when art is available the GLB geometry is
     * swapped into the InstancedMesh asynchronously (drop-in). */
    store?: ModelStore | null;
  } = {},
): FormationView {
  const slots = computeFormationSlots(def, count);
  const group = new THREE.Group();
  group.name = label;
  group.userData.selectable = true;
  group.userData.label = label;

  // Far LOD: capsule stand-ins (the pre-existing look). Near LOD swaps in
  // the skinned humanoid below; one InstancedMesh either way.
  const mat = new THREE.MeshStandardMaterial({ color: tint, roughness: 0.8 });
  const mesh = new THREE.InstancedMesh(
    sharedCapsuleGeo,
    mat,
    Math.max(count, 1),
  );
  mesh.castShadow = true;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.frustumCulled = false;
  // Raycast against the group: add an invisible bounding box mesh for picking.
  const picker = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshBasicMaterial({ visible: false }),
  );
  picker.name = `${label}__picker`;
  group.add(mesh);
  group.add(picker);
  group.userData.picker = picker;

  const positions = slots.map((s) => slotToWorld(s, anchor.x, anchor.z, heading));
  const goals = positions.map((p) => ({ ...p }));
  // Bounding radius from the slot layout (formation-local, pre-heading).
  let radius = 2;
  {
    let mx = 0, mz = 0;
    for (const s of slots) { mx += s.x; mz += s.z; }
    mx /= Math.max(slots.length, 1); mz /= Math.max(slots.length, 1);
    for (const s of slots) radius = Math.max(radius, Math.hypot(s.x - mx, s.z - mz));
  }
  // Near-LOD skinned humanoid (one geometry per formation: the per-instance
  // animation attributes can't be shared across InstancedMeshes).
  const animSys = opts.anim ?? null;
  let anim: AnimController | null = null;
  let animAttrs: AnimAttributes | null = null;
  let skinGeo: THREE.BufferGeometry | null = null;
  let skinMat: THREE.MeshStandardMaterial | null = null;
  if (animSys) {
    skinGeo = buildHumanoidGeometry();
    animAttrs = attachAnimAttributes(skinGeo, Math.max(count, 1));
    skinMat = makeSkinnedMaterial(
      animSys.texture,
      BONE_COUNT,
      FRAMES_PER_CLIP,
    );
    skinMat.color.set(tint);
    // Combatants use the generic clip set (only the faber has real clips).
    anim = new AnimController(animSys, false);
  }
  const animPhase = new Float32Array(Math.max(count, 1));
  for (let i = 0; i < animPhase.length; i++) {
    animPhase[i] = (i * 0.61803398875) % 1;
  }
  const view: FormationView = {
    group, mesh, slots, goals, positions,
    heading, anchor: { ...anchor }, label, def,
    unit: opts.unit ?? null,
    team: opts.team ?? 0,
    radius,
    artKey: opts.art
      ? artKeyFor(opts.art, opts.unit?.id ?? label)
      : `placeholder:${opts.unit?.id ?? label}`,
    march: null,
    anim,
    animSys,
    animAttrs,
    skinGeo,
    skinMat,
    capsuleMat: mat,
    animPhase,
    animMoving: false,
    animMelee: false,
    animPace: "march",
    animDefensive: false,
    meleeMem: makeMeleeAnimMemory(),
    roadColumn: false,
    roadBonus: 1,
    onRelease: null,
    lodNear: false,
    unitGeo: null,
    unitMat: null,
    unitLodGeo: null,
    model: opts.art ?? null,
    realGeometry: false,
  };
  // Vendored-GLB drop-in: the capsule/skinned fallbacks render until the
  // model arrives, then the merged static geometry feeds the same
  // InstancedMesh (instancing strategy unchanged).
  if (opts.art?.available && opts.store) {
    upgradeFormationModel(view, opts.art, opts.store, tint);
  }
  layoutFormation(view, groundY);
  return view;
}

/**
 * Async GLB upgrade for a formation: merge the vendored model's static
 * geometry into the existing InstancedMesh. One draw call, faction tint —
 * the procedural capsule/skinned fallbacks stay until the load resolves,
 * and stay permanently if it fails.
 *
 * LOD plan: the base GLB feeds the near tier; the highest vendored
 * art-pipeline LOD tier (model.lodPaths, e.g. imperial_kit LOD3) feeds the
 * far tier. With no LOD tier vendored, the far tier stays the capsule.
 */
export function upgradeFormationModel(
  view: FormationView,
  model: ResolvedModel,
  store: ModelStore,
  tint: number,
): void {
  const key = artKeyFor(model, view.unit?.id ?? view.label);
  void store.load(model).then((scene) => {
    if (!scene) return; // keep the placeholder fallbacks
    if (view.model && artKeyFor(view.model, view.unit?.id ?? view.label) !== key) return;
    try {
      const geo = extractStaticGeometry(scene);
      if (!geo) return;
      const mat = new THREE.MeshStandardMaterial({ color: tint, roughness: 0.85 });
      view.unitGeo = geo;
      view.unitMat = mat;
      view.mesh.geometry = geo;
      view.mesh.material = mat;
      view.mesh.castShadow = true;
      view.realGeometry = true;
    } catch (e) {
      console.warn(`[units] failed to instance ${model.webPath}: ${String(e)}`);
    }
  });
  // Far-LOD tier: highest-numbered vendored LOD (lodPaths is tier-ordered).
  const lodPath = model.lodPaths.length > 0 ? model.lodPaths[model.lodPaths.length - 1] : null;
  if (lodPath) {
    void store.loadByUrl(lodPath).then((scene) => {
      if (!scene) return;
      if (view.model && artKeyFor(view.model, view.unit?.id ?? view.label) !== key) return;
      try {
        const geo = extractStaticGeometry(scene);
        if (geo) view.unitLodGeo = geo;
      } catch (e) {
        console.warn(`[units] failed to instance LOD ${lodPath}: ${String(e)}`);
      }
    });
  }
}

function layoutFormation(view: FormationView, groundY: (x: number, z: number) => number): void {
  const { positions, mesh } = view;
  for (let i = 0; i < positions.length; i++) {
    const p = positions[i];
    dummy.position.set(p.x, groundY(p.x, p.z), p.z);
    dummy.rotation.set(0, view.heading, 0);
    dummy.updateMatrix();
    mesh.setMatrixAt(i, dummy.matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  // Fit the invisible picker to the formation footprint.
  const picker = view.group.userData.picker as THREE.Mesh;
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const p of positions) {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
    minZ = Math.min(minZ, p.z); maxZ = Math.max(maxZ, p.z);
  }
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  picker.position.set(cx, groundY(cx, cz) + 1, cz);
  picker.scale.set(Math.max(maxX - minX + 2, 2), 3, Math.max(maxZ - minZ + 2, 2));
  picker.updateMatrixWorld();
}

/**
 * Advance soldiers toward their goals. With an active march, the formation
 * anchor advances along the shared flow field and soldier goals track
 * slot offsets around the moving anchor; without one this is the legacy
 * direct steering toward per-soldier goals.
 */
export function updateFormationMovement(
  view: FormationView,
  dt: number,
  groundY: (x: number, z: number) => number,
  speedOverride?: number,
  marchCtx?: MarchContext,
): void {
  // Road state first: it narrows the formation to a 4-abreast column on
  // roads (restoring the battle layout off-road) and feeds formationSpeed.
  updateFormationRoadState(view, marchCtx?.roads ?? null);
  const speed = speedOverride ?? formationSpeed(view);
  const m = view.march;
  if (m) {
    if (marchCtx) {
      // Refresh a stale shared field; the repath cadence lives in NavSystem.
      if (
        marchCtx.simNow - m.field.builtAt >=
        marchCtx.nav.t.repathSeconds
      ) {
        const f = marchCtx.nav.requestField(
          m.team,
          m.destX,
          m.destZ,
          marchCtx.simNow,
        );
        if (f) m.field = f;
      }
      const r = marchCtx.nav.advance(m.field, m.ax, m.az, speed, dt);
      m.ax = r.x;
      m.az = r.z;
      if (Math.hypot(r.dx, r.dz) > 1e-6) {
        view.heading = Math.atan2(r.dx, r.dz);
      }
      if (r.arrived) {
        // Snap goals to the final formation shape at the (field-snapped)
        // goal; soldiers steer the last metres themselves — no teleport.
        for (let i = 0; i < view.slots.length; i++) {
          const w = slotToWorld(
            view.slots[i],
            m.field.goalX,
            m.field.goalZ,
            m.faceHeading,
          );
          view.goals[i].x = w.x;
          view.goals[i].z = w.z;
        }
        view.heading = m.faceHeading;
        view.march = null;
      } else {
        for (let i = 0; i < view.slots.length; i++) {
          const w = slotToWorld(view.slots[i], m.ax, m.az, view.heading);
          view.goals[i].x = w.x;
          view.goals[i].z = w.z;
        }
      }
    } else {
      // March state without a nav context (shouldn't happen in-game):
      // degrade to the legacy direct move so the formation still arrives.
      for (let i = 0; i < view.slots.length; i++) {
        const w = slotToWorld(view.slots[i], m.destX, m.destZ, m.faceHeading);
        view.goals[i].x = w.x;
        view.goals[i].z = w.z;
      }
      view.march = null;
    }
  }
  let dirty = false;
  for (let i = 0; i < view.positions.length; i++) {
    const p = view.positions[i];
    const g = view.goals[i];
    const dx = g.x - p.x;
    const dz = g.z - p.z;
    const dist = Math.hypot(dx, dz);
    if (dist > 0.15) {
      const step = Math.min(dist, speed * dt);
      p.x += (dx / dist) * step;
      p.z += (dz / dist) * step;
      dirty = true;
    }
  }
  // The formation anchor follows the mean position; heading faces movement.
  if (dirty) {
    let mx = 0, mz = 0;
    for (const p of view.positions) { mx += p.x; mz += p.z; }
    view.anchor.x = mx / view.positions.length;
    view.anchor.z = mz / view.positions.length;
    layoutFormation(view, groundY);
  }
  // Animation input: the formation reads as walking while any soldier is
  // still steering (this is what the render layer turns into the walk clip).
  view.animMoving = dirty;
}

/** Movement speed for a formation: unit data wins, else formation default.
 * On a road the canonical `path_modifiers.move_speed` bonus applies,
 * stacking multiplicatively with the marching_column formation's own
 * `road_speed_bonus` data modifier. */
export function formationSpeed(view: FormationView): number {
  const unitSpeed = view.unit?.extra["move_speed"];
  const base =
    typeof unitSpeed === "number"
      ? unitSpeed
      : 6 * (view.def.modifiers.move_speed ?? 1);
  if (!view.roadColumn) return base;
  const formationBonus = view.def.modifiers.road_speed_bonus ?? 1;
  return base * view.roadBonus * formationBonus;
}

/**
 * Road state for a formation: while the march anchor is on a road the
 * formation narrows to a 4-abreast marching column (it resumes its
 * battle layout off-road). Called from updateFormationMovement, which
 * owns the sim-tick cadence; speed reads the flag set here.
 */
export function updateFormationRoadState(
  view: FormationView,
  roads: RoadNetwork | null | undefined,
): void {
  if (!roads) {
    if (view.roadColumn) {
      view.roadColumn = false;
      view.roadBonus = 1;
      const layout = computeFormationSlots(view.def, view.slots.length);
      for (let i = 0; i < view.slots.length; i++) {
        if (layout[i]) view.slots[i] = layout[i];
      }
    }
    return;
  }
  const p = view.march ? { x: view.march.ax, z: view.march.az } : view.anchor;
  const bonus = roads.moveBonusAt(p.x, p.z);
  const onRoad = bonus > 1;
  view.roadBonus = bonus;
  if (onRoad === view.roadColumn) return;
  view.roadColumn = onRoad;
  const n = view.slots.length;
  const layout = onRoad
    ? computeRoadColumnSlots(n, view.def.spacing)
    : computeFormationSlots(view.def, n);
  for (let i = 0; i < n; i++) {
    if (layout[i]) view.slots[i] = layout[i];
  }
}

/**
 * LOD swap for a formation: near the camera the InstancedMesh renders the
 * skinned humanoid, far away the static capsule. One draw call either way;
 * only vertex cost changes. Hysteresis avoids boundary flicker.
 *
 * When a vendored GLB is attached (unitGeo), the near tier renders it; the
 * far tier renders the highest vendored art-pipeline LOD tier (unitLodGeo),
 * falling back to the capsule when no LOD tier is vendored.
 */
export function updateFormationLOD(
  view: FormationView,
  camX: number,
  camZ: number,
): void {
  if (view.unitGeo && view.unitMat) {
    const d = Math.hypot(view.anchor.x - camX, view.anchor.z - camZ);
    const near = d < ANIM_LOD_DISTANCE * (view.lodNear ? 1.08 : 0.92);
    const farGeo = view.unitLodGeo ?? sharedCapsuleGeo;
    const wantGeo = near ? view.unitGeo : farGeo;
    const wantMat = near || view.unitLodGeo ? view.unitMat : view.capsuleMat;
    // The != on geometry (not just the near flag) catches the async moment
    // a vendored LOD tier arrives while the view is already far.
    if (near === view.lodNear && view.mesh.geometry === wantGeo) return;
    view.lodNear = near;
    view.mesh.geometry = wantGeo;
    view.mesh.material = wantMat;
    return;
  }
  if (!view.skinGeo || !view.skinMat) return;
  const d = Math.hypot(view.anchor.x - camX, view.anchor.z - camZ);
  const near =
    d < ANIM_LOD_DISTANCE * (view.lodNear ? 1.08 : 0.92);
  if (near === view.lodNear) return;
  view.lodNear = near;
  view.mesh.geometry = near ? view.skinGeo : sharedCapsuleGeo;
  view.mesh.material = near ? view.skinMat : view.capsuleMat;
}

/**
 * Rising-edge melee hook for the sim tick: pilum-armed formations open
 * with the throw clip on first contact. Call when inMelee goes false->true.
 */
export function noteMeleeContact(view: FormationView): void {
  const equipment = view.unit?.extra["equipment"] as
    | readonly string[]
    | undefined;
  beginMeleeEngagement(view.meleeMem, equipment);
}

/**
 * Advance the formation's shared animation state and write per-instance
 * poses (clip slots + frames + blend). Called once per render frame; the
 * heavy lifting (bone matrices) already lives in the baked atlas.
 *
 * Melee activity is resolved data-driven from the unit's equipment list
 * (pilum -> throw once, draw, then sidearm; hasta -> spear cycle); the
 * throw clip's timed "release" event fires view.onRelease for the volley
 * tracer (render/volley.ts).
 */
export function updateFormationAnim(view: FormationView, dt: number): void {
  if (!view.anim || !view.animSys) return;
  const equipment = view.unit?.extra["equipment"] as readonly string[] | undefined;
  const melee = resolveMeleeActivity(
    view.meleeMem,
    dt,
    equipment,
    view.animDefensive,
  );
  const pose = view.anim.update(
    dt,
    formationActivity({
      moving: view.animMoving,
      inMelee: view.animMelee,
      pace: view.animPace,
      defensive: view.animDefensive,
      melee,
    }),
  );
  for (const e of pose.events) {
    if (e === "release") {
      view.onRelease?.(view.anchor.x, view.anchor.z, view.heading);
    }
  }
  // GLB-geometry formations have no per-instance bone attributes, but the
  // state machine (and its timed events, e.g. the pilum release) still runs.
  if (!view.animAttrs) return;
  const sys = view.animSys;
  const durA = sys.durationOf(pose.a);
  const durB = sys.durationOf(pose.b);
  const n = view.mesh.count;
  for (let i = 0; i < n; i++) {
    const phase = view.animPhase[i] ?? 0;
    const tA = (pose.timeA + phase * durA) % durA;
    const tB = (pose.timeB + phase * durB) % durB;
    writeAnimPose(
      view.animAttrs,
      i,
      pose.a,
      (tA / durA) * (FRAMES_PER_CLIP - 1),
      pose.b,
      (tB / durB) * (FRAMES_PER_CLIP - 1),
      pose.blend,
    );
  }
  flagAnimAttributes(view.animAttrs);
}

/**
 * Shrink a formation to `alive` soldiers after casualties. Truncates the
 * soldier arrays and clamps the instanced mesh; the dead simply vanish
 * (death animation is a Phase 0 asset concern).
 */
export function setFormationCount(
  view: FormationView,
  alive: number,
  groundY: (x: number, z: number) => number,
): void {
  const n = Math.max(0, Math.min(alive, view.positions.length));
  view.positions.length = n;
  view.goals.length = n;
  view.slots.length = n;
  view.mesh.count = n;
  if (n > 0) {
    // Re-sync the anchor to the surviving soldiers: truncation keeps the
    // first N slots (e.g. a corner of the old footprint), so without this
    // the anchor stays at the old formation center while the visible
    // soldiers — and the picking box — are elsewhere.
    let mx = 0,
      mz = 0;
    for (const p of view.positions) {
      mx += p.x;
      mz += p.z;
    }
    view.anchor.x = mx / n;
    view.anchor.z = mz / n;
    layoutFormation(view, groundY);
  }
}

/**
 * Retarget a formation's movement.
 *
 * With a MarchContext the formation anchor becomes a flow-field pathing
 * agent: a shared NavSystem field is requested for (team, destination)
 * and the anchor marches along it (see updateFormationMovement).
 * Without one, every soldier's goal is retargeted directly (legacy).
 *
 * NB: the anchor is NOT moved here. It tracks the mean soldier position
 * (recomputed in updateFormationMovement); teleporting it to the
 * destination corrupts range checks (inMeleeRange/inBuildingRange), AI
 * leash/aggro math, and health-bar placement for a tick.
 */
export function orderFormationMove(
  view: FormationView,
  destX: number,
  destZ: number,
  heading: number,
  marchCtx?: MarchContext,
  pace: MarchPace = "march",
): void {
  view.heading = heading;
  // Fresh orders re-arm pila and set the march pace (gait clip).
  view.animPace = pace;
  rearmPilum(view.meleeMem);
  if (marchCtx) {
    const field = marchCtx.nav.requestField(
      marchCtx.team,
      destX,
      destZ,
      marchCtx.simNow,
    );
    if (field) {
      view.march = {
        destX,
        destZ,
        faceHeading: heading,
        team: marchCtx.team,
        field,
        ax: view.anchor.x,
        az: view.anchor.z,
      };
      return;
    }
    // No passable goal nearby: fall through to direct steering.
    view.march = null;
  } else {
    view.march = null;
  }
  for (let i = 0; i < view.slots.length; i++) {
    const w = slotToWorld(view.slots[i], destX, destZ, heading);
    view.goals[i].x = w.x;
    view.goals[i].z = w.z;
  }
}
